import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { SECTOR_PROFILES } from '@/lib/sectorProfiles';
import { ChevronDown, ChevronUp, FileText, AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { AA_IMPACT_LOGO_URL } from '@/lib/constants';

export default function SectorProfileCards() {
  const [expandedSector, setExpandedSector] = useState(null);

  return (
    <div className="mt-8" data-testid="sector-profiles-section">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <img src={AA_IMPACT_LOGO_URL} alt="AA Impact" className="h-8 w-8 rounded-lg object-cover" />
          <div>
            <h2 className="text-xl font-bold text-white">AA Impact Sector Intelligence Profiles</h2>
            <p className="mt-1 text-sm text-white/40">
              50 AI-powered workflows across 5 sectors. Regulatory-driven sustainability analytics for enterprise decision-makers.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {SECTOR_PROFILES.map((sector, index) => {
          const Icon = sector.icon;
          const isExpanded = expandedSector === sector.id;

          return (
            <motion.div
              key={sector.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06, duration: 0.25 }}
            >
              <div
                className="overflow-hidden rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] shadow-[0_10px_30px_rgba(0,0,0,0.32)] transition-colors duration-150 hover:border-white/20"
                style={{ borderLeftWidth: '4px', borderLeftColor: sector.accentColor }}
              >
                <button
                  onClick={() => setExpandedSector(isExpanded ? null : sector.id)}
                  data-testid={`sector-profile-toggle-${sector.id}`}
                  className="flex w-full items-center gap-4 p-5 text-left"
                >
                  <div
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${sector.accentColor}18`, color: sector.accentColor }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-white">{sector.name}</h3>
                      <Badge
                        variant="outline"
                        className="text-[10px]"
                        style={{ color: sector.accentColor, borderColor: `${sector.accentColor}40` }}
                      >
                        {sector.code}
                      </Badge>
                      <Badge className="border border-white/10 bg-white/5 text-[10px] text-white/60">
                        {sector.totalWorkflows} workflows
                      </Badge>
                    </div>
                    <p className="mt-1 hidden text-xs text-white/40 md:block">{sector.description}</p>
                  </div>

                  <div className="hidden text-center sm:block">
                    <p className="text-[10px] uppercase tracking-wider text-white/30">{sector.headlineMetric.label}</p>
                    <p className="font-mono text-xl font-bold tabular-nums" style={{ color: sector.accentColor }}>
                      {sector.headlineMetric.value}
                    </p>
                    <p className="text-[10px] text-white/30">{sector.headlineMetric.sub}</p>
                  </div>

                  <div className="hidden gap-1 lg:flex">
                    {sector.keyRegulations.slice(0, 3).map((reg) => (
                      <Badge key={reg} className="border border-white/10 bg-white/[0.04] text-[9px] text-white/50">
                        {reg}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-white/40" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-white/40" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-4 border-t border-white/5 px-5 py-4">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                            <p className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/35">
                              <Users className="h-3 w-3" /> Target archetypes
                            </p>
                            <div className="space-y-1">
                              {sector.targetArchetypes.map((archetype) => (
                                <p key={archetype} className="text-xs text-white/60">{archetype}</p>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                            <p className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/35">
                              <FileText className="h-3 w-3" /> Key regulations
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {sector.keyRegulations.map((reg) => (
                                <Badge
                                  key={reg}
                                  className="border text-[10px] text-white/70"
                                  style={{ borderColor: `${sector.accentColor}30`, backgroundColor: `${sector.accentColor}10` }}
                                >
                                  {reg}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                            <p className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/35">
                              <TrendingUp className="h-3 w-3" /> Headline outcome
                            </p>
                            <p className="font-mono text-2xl font-bold" style={{ color: sector.accentColor }}>
                              {sector.headlineMetric.value}
                            </p>
                            <p className="text-xs text-white/40">
                              {sector.headlineMetric.label} {sector.headlineMetric.sub}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-white/35">
                            Top workflows
                          </p>
                          <div className="space-y-2">
                            {sector.workflows.map((wf) => (
                              <div
                                key={wf.id}
                                className="rounded-xl border border-white/10 bg-[#0B1E31] p-3"
                                data-testid={`workflow-card-${wf.id}`}
                              >
                                <div className="flex items-start gap-3">
                                  <Badge
                                    className="mt-0.5 flex-shrink-0 border text-[10px] font-mono"
                                    style={{ borderColor: `${sector.accentColor}40`, color: sector.accentColor, backgroundColor: `${sector.accentColor}12` }}
                                  >
                                    {wf.id}
                                  </Badge>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-white">{wf.name}</p>
                                    <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                                      <div>
                                        <p className="flex items-center gap-1 text-[10px] uppercase text-white/30">
                                          <AlertTriangle className="h-3 w-3 text-[#FF6B6B]" /> Pain point
                                        </p>
                                        <p className="mt-1 text-xs text-white/55">{wf.painPoint}</p>
                                      </div>
                                      <div>
                                        <p className="flex items-center gap-1 text-[10px] uppercase text-white/30">
                                          <TrendingUp className="h-3 w-3 text-[#2A9D8F]" /> Outcome
                                        </p>
                                        <p className="mt-1 text-xs text-white/55">{wf.outcome}</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] uppercase text-white/30">Regulators</p>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                          {wf.regulators.map((reg) => (
                                            <span key={reg} className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[9px] text-white/45">
                                              {reg}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
